import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Snackbar,
  TextField,
} from '@mui/material';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../app/LanguageContext';
import { importMakerworldProduct } from './makerworldImportApi';
import { isValidMakerworldModelUrl, normalizeMakerworldUrl } from './makerworldImportUtils';
import { invalidateProductSkuPickerCache } from '../../modules/products/private/productSkuPickerCache';

export default function MakerworldImportDialog({ open, onClose }) {
  const { translate } = useTranslation();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' });

  const handleClose = () => {
    if (importing) return;
    setUrl('');
    onClose();
  };

  const handleImport = async () => {
    const normalized = normalizeMakerworldUrl(url);
    if (!isValidMakerworldModelUrl(normalized)) {
      setSnackbar({
        open: true,
        message: translate('extensions.makerworld_import.invalid_url'),
        severity: 'error',
      });
      return;
    }

    setImporting(true);
    try {
      const res = await importMakerworldProduct({ url: normalized });
      const payload = res?.data;
      const code = payload?.code || payload?.id;
      if (!code) {
        throw new Error(translate('extensions.makerworld_import.import_failed'));
      }
      invalidateProductSkuPickerCache();
      onClose();
      setUrl('');
      navigate(`/products/${encodeURIComponent(code)}/edit`);
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.message || translate('extensions.makerworld_import.import_failed'),
        severity: 'error',
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{translate('extensions.makerworld_import.dialog_title')}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={translate('extensions.makerworld_import.url_label')}
              placeholder={translate('extensions.makerworld_import.url_placeholder')}
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              disabled={importing}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !importing) {
                  e.preventDefault();
                  void handleImport();
                }
              }}
            />
            {importing ? (
              <Box>
                <LinearProgress />
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  {translate('extensions.makerworld_import.importing_hint')}
                </Alert>
              </Box>
            ) : (
              <Alert severity="info">{translate('extensions.makerworld_import.dialog_hint')}</Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={importing}>
            {translate('common.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleImport()}
            disabled={importing || !url.trim()}
            startIcon={<CloudDownloadOutlinedIcon />}
          >
            {translate('extensions.makerworld_import.import_button')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}
